import { DataMapper } from '../api/transport';
import { diContainer, serviceNames } from '../diContainer/diContainer';
import AnalyticsRepository from '../repositories/AnalyticsRepository';
import ConfigRepository from '../repositories/ConfigRepository';
import Config, { IConfig } from '../domain/entities/Config/Config';
import Api from '../api';
import defaultTheme from '../config/default-theme';
import UIState from '../stores/UIState';
import ConfigUtils from '../utils/ConfigUtils';
import OptimizelyService from '../application/services/OptimizelyService';
import Routes, { ExternalRoute } from '../application/routes/Routes';
import { LogServiceError } from '../application/services/LogServiceError';
import { getDisplayConfig } from '../config/display/DisplayConfig';

export default class InitAppUseCase {
  private uiState: UIState;

  private analyticsRepository: AnalyticsRepository;

  private dataMapper: DataMapper;

  private configRepository: ConfigRepository;

  private apiSdk: Api;

  private optimizelyService: OptimizelyService;

  private routes: Routes;

  constructor() {
    this.configRepository = diContainer.get(serviceNames.configRepository);
    this.uiState = diContainer.get(serviceNames.uiState);
    this.analyticsRepository = diContainer.get(serviceNames.analyticsRepository);
    this.dataMapper = diContainer.get(serviceNames.apiDataMapper);
    this.apiSdk = diContainer.get(serviceNames.apiSdk);
    this.optimizelyService = diContainer.get(serviceNames.optimizelyService);
    this.routes = diContainer.get(serviceNames.routes);
  }

  private setTheme(): void {
    const { documentElement } = document;
    Object.keys(defaultTheme.colors).forEach((color: string) => {
      documentElement.style.setProperty(`--color-${color}`, defaultTheme.colors[color]);
    });
    Object.keys(defaultTheme.fonts).forEach(font => {
      documentElement.style.setProperty(`--${font}`, defaultTheme.fonts[font]);
    });
    Object.keys(defaultTheme.spacings).forEach(breakpoint => {
      Object.keys(defaultTheme.spacings[breakpoint]).forEach(position => {
        documentElement.style.setProperty(`--space-${breakpoint}-${position}`, defaultTheme.spacings[breakpoint][position]);
      });
    });
  }

  private init(): void {
    const dataConfig = ConfigUtils.getFromDataConfigAttr();
    const mappedConfig = this.dataMapper.mapResponse('siteConfigMapper', dataConfig) as IConfig;
    const config = Config.fromJSON(mappedConfig);
    this.apiSdk.setup({
      baseUrl: config.baseUrl,
      brandId: config.brandId,
      countryId: config.countryId,
      environment: config.stardustServiceConfig.environment,
      isProd: config.isProduction,
      regionId: config.regionId,
    });
    if (!Number.isInteger(config.brandId) || !Number.isInteger(config.regionId)) {
      this.routes.redirect(ExternalRoute.CART);
    }
    this.configRepository.setConfig(config);
    this.setupDisplayConfig();
    this.optimizelyService.init();
    LogServiceError.instance.initialize(config.environment);
  }

  private setupDisplayConfig = (): void => {
    const { brandName, regionName } = this.configRepository.config;
    const displayConfig = getDisplayConfig(brandName, regionName);
    this.configRepository.config.displayConfig = displayConfig;
  };

  execute = async (): Promise<void> => {
    window.addEventListener('resize', () => this.uiState.resizeHandler());
    
    this.init();

    this.setTheme();
    this.analyticsRepository.initAnalytics();
  };
}
